/**
 * Shared per-metric formula functions for brand-page citation stats.
 *
 * Originally extracted from the "Your Cited Pages" card so it and the
 * content table shared one definition. The card survives only behind
 * `unified-content-table` being off; the content table is the primary
 * consumer, alongside the page-detail and prompt-detail routes and the
 * `useBrandPageCitations` hook — so this is still the single
 * definition, with more readers rather than fewer.
 *
 * All functions accept BrandPageEngineStat (or any structurally-compatible
 * object) and return pure computed values — no React, no Supabase.
 */

import { roundHalfUp } from "@ripenn/visibility-scoring";
import type { BrandPageEngineStat } from "@/lib/visibility/types";

/** Below this ratio, consistency is flagged amber. */
export const CONSISTENCY_LOW_THRESHOLD = 0.4;

/**
 * Minimum repeat count to surface consistency. One repeat in one scan
 * is always 100% by construction and tells the user nothing useful —
 * show "—" instead so the column is honest about signal.
 */
export const CONSISTENCY_MIN_REPEATS = 2;

/** Below this ratio, trust is flagged as a gap (amber ⚠). */
export const TRUST_GAP_THRESHOLD = 0.3;

/** Minimum scansEither for the trust ratio to be meaningful. */
export const TRUST_GAP_MIN_SCANS = 2;

/**
 * Fraction of total scans in which this page was cited.
 * Returns 0 when totalScans is 0.
 */
export function citationRate(
  stat: Pick<BrandPageEngineStat, "scansCited" | "totalScans">,
): number {
  return stat.totalScans > 0 ? stat.scansCited / stat.totalScans : 0;
}

/**
 * Fraction of total scans in which this page was retrieved (fetched
 * during web search, whether or not it was cited).
 * Returns 0 when totalScans is 0.
 */
export function retrievalRate(
  stat: Pick<BrandPageEngineStat, "scansRetrieved" | "totalScans">,
): number {
  return stat.totalScans > 0 ? stat.scansRetrieved / stat.totalScans : 0;
}

/**
 * When the page surfaced for a prompt, how reliably did the engine cite
 * it across repeats? repeatsCited / repeatsInSurfacingScans.
 * Returns null when the sample is too small (< CONSISTENCY_MIN_REPEATS).
 */
export function consistencyRatio(
  stat: Pick<BrandPageEngineStat, "repeatsCited" | "repeatsInSurfacingScans">,
): number | null {
  if (stat.repeatsInSurfacingScans < CONSISTENCY_MIN_REPEATS) return null;
  return stat.repeatsCited / stat.repeatsInSurfacingScans;
}

/**
 * Whether the trust ratio (scansCited / scansEither) carries enough
 * signal to display. Requires retrieval data, at least one retrieval,
 * and a minimum sample to distinguish "always cited" from "always cited
 * because lucky."
 */
export function isTrustMeaningful(
  stat: Pick<
    BrandPageEngineStat,
    "hasRetrievalData" | "scansRetrieved" | "scansEither"
  >,
): boolean {
  return (
    stat.hasRetrievalData &&
    stat.scansRetrieved > 0 &&
    stat.scansEither >= TRUST_GAP_MIN_SCANS
  );
}

/**
 * Registry `trustRatio`: scansCited / scansEither, null when the page
 * never surfaced. The one place this ratio is computed; the hook that
 * builds BrandPageEngineStat and every aggregate call this.
 */
export function trustRatioOf(
  scansCited: number,
  scansEither: number,
): number | null {
  return scansEither > 0 ? scansCited / scansEither : null;
}

/**
 * Whether the trust ratio is meaningful and below the gap threshold.
 * Pages and the card show the amber warning from this, not from a
 * local copy of the rule.
 */
export function hasTrustGap(
  stat: Pick<
    BrandPageEngineStat,
    "hasRetrievalData" | "scansRetrieved" | "scansEither" | "trustRatio"
  >,
): boolean {
  return (
    isTrustMeaningful(stat) &&
    stat.trustRatio !== null &&
    stat.trustRatio < TRUST_GAP_THRESHOLD
  );
}

/**
 * Citation rate across several engine stats: SUM(scansCited) /
 * SUM(totalScans), never an average of per-engine rates. Returns 0
 * when there are no scans, matching `citationRate`.
 */
export function citationRateAcross(
  stats: readonly Pick<BrandPageEngineStat, "scansCited" | "totalScans">[],
): number {
  let cited = 0;
  let total = 0;
  for (const s of stats) {
    cited += s.scansCited;
    total += s.totalScans;
  }
  return total > 0 ? cited / total : 0;
}

/**
 * Registry `pageCitationRate` as every surface shows it: the ratio
 * (0-1) of `citationRate`, rounded half-up to three places. The one
 * rounding path for the page header, the content list and the
 * search-divergence list.
 */
export function pageCitationRateOf(
  stat: Pick<BrandPageEngineStat, "scansCited" | "totalScans">,
): number {
  return roundHalfUp(citationRate(stat), 3);
}

/** `pageCitationRateOf` across engines (SUM then divide). */
export function pageCitationRateAcross(
  stats: readonly Pick<BrandPageEngineStat, "scansCited" | "totalScans">[],
): number {
  return roundHalfUp(citationRateAcross(stats), 3);
}
